import { Copy } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { copyText } from './clipboard';

/** How long a copy confirmation stays visible on the button. */
const COPIED_CONFIRMATION_MS = 2000;

export interface InviteDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** The join URL; never contains a token. */
  readonly link: string;
  /** The human-passable access code: the room slug. */
  readonly code: string;
}

/**
 * The Syncra invite dialog: the readonly room link with a Копировать button (the copy-link
 * affordance of the control bar moved here), the access-code row (the room slug) with its own
 * copy button, and the guest/expiry notes. The «Справочник · кто на связи» contacts block of
 * the mockup is omitted: there is no contacts directory backend.
 */
export function InviteDialog(props: InviteDialogProps): JSX.Element {
  const { t } = useTranslation();
  const [copied, setCopied] = useState<'link' | 'code' | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, []);

  function confirmCopied(which: 'link' | 'code' | null): void {
    setCopied(which);
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
    }
    timerRef.current = window.setTimeout(() => {
      setCopied(null);
    }, COPIED_CONFIRMATION_MS);
  }

  async function copy(value: string, which: 'link' | 'code'): Promise<void> {
    confirmCopied((await copyText(value)) ? which : null);
  }

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent
        data-testid="invite-dialog"
        className="border-white/10 bg-zinc-900 text-zinc-100 sm:max-w-[440px]"
      >
        <DialogHeader>
          <DialogTitle className="text-[16px] font-semibold tracking-tight text-white">
            {t('invite.title')}
          </DialogTitle>
          <DialogDescription className="text-[12.5px] text-zinc-400">
            {t('invite.guests_note')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5">
          <div className="flex gap-2">
            <input
              readOnly
              data-testid="room-url"
              value={props.link}
              aria-label={t('invite.link_aria')}
              onFocus={(event) => {
                event.currentTarget.select();
              }}
              className="h-9 min-w-0 flex-1 rounded-lg border border-white/10 bg-white/[0.05] px-3 font-mono text-[12px] text-zinc-200 outline-none focus-visible:border-white/20"
            />
            <button
              type="button"
              data-testid="copy-link"
              onClick={() => {
                void copy(props.link, 'link');
              }}
              className="flex h-9 shrink-0 items-center gap-1.5 rounded-lg bg-white px-3 text-[12.5px] font-semibold text-zinc-950 transition-colors hover:bg-zinc-200"
            >
              <Copy className="size-3.5" />
              {copied === 'link' ? t('invite.copied') : t('invite.copy')}
            </button>
          </div>
          <div className="flex items-center justify-between gap-2 rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2">
            <span className="text-[12.5px] text-zinc-400">
              {t('invite.code')}{' '}
              <span
                data-testid="invite-code"
                className="font-mono font-semibold tracking-wide text-zinc-100"
              >
                {props.code}
              </span>
            </span>
            <button
              type="button"
              data-testid="invite-code-copy"
              onClick={() => {
                void copy(props.code, 'code');
              }}
              aria-label={t('invite.code_copy_aria')}
              title={t('invite.code_copy_aria')}
              className="flex size-7 shrink-0 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-white/10 hover:text-white"
            >
              <Copy className="size-3.5" />
            </button>
          </div>
          <p className="text-[11px] leading-none text-zinc-500">{t('invite.link_note')}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
